import {createConsumer, Consumer, Connection, INTERNAL} from '@rails/actioncable';
import constants from '@/modules/constants';
import {cookieManager} from '@skiwo/sk-basic-components';

const authTokenName = `${process.env.NODE_ENV}_authToken`;
const currentToken = () => cookieManager.getCookie(authTokenName) || localStorage[authTokenName] || '';

const defaultChannelHandler = {
  connected() {
    // eslint-disable-next-line
    console.log('connected', this.identifier);
  },
  disconnected() {
    // eslint-disable-next-line
    console.log('disconnected', this.identifier);
  },
  rejected() {
    // eslint-disable-next-line
    console.log('rejected', this.identifier);
  },
  received(data) {
    // eslint-disable-next-line
    console.log(data, this.identifier);
    this.callback(data, this.identifier);
  },
  callback(data) {
    // eslint-disable-next-line
    console.log('default callback', data);
  },
  appear() {
    // eslint-disable-next-line
    console.log('Attempting to re-appear', this.identifier);
    this.perform('appear', {web_page: window.location.href});
  }
};

// NOTE: this file patches @rails/actioncable internals that are not public API:
// Consumer#ensureActiveConnection, connection.events, connection.uninstallEventHandlers(),
// connection.monitor.stop(), connection.webSocket, connection.disconnected and
// subscriptions.notifyAll(). The package is pinned to an exact
// version in package.json for that reason; recheck these places whenever it is upgraded.
//
// One consumer (one socket) per app. Subscriptions live on it across token changes,
// ActionCable resubscribes them every time the socket is opened again.
let consumer = null;
// Token the current socket was opened with
let socketToken = '';
// Token the server refused. The socket is never opened with it again.
let rejectedToken = '';

const webSocket = {
  // Called when the server refuses the current token, see ssr-client.js
  onUnauthorized() {},
  consumer() {
    if (!consumer) {
      consumer = createConsumer(() => {
        socketToken = currentToken();
        return constants.WEB_SOCKET_PATH + '?jwt=' + socketToken;
      });
      // Without a token, or with a refused one, the server can only reject the connection again
      consumer.ensureActiveConnection = function ensureActiveConnection() {
        const token = currentToken();
        if (token && token !== rejectedToken && this.subscriptions.subscriptions.length) {
          Consumer.prototype.ensureActiveConnection.call(this);
        }
      };
      consumer.connection.events = {
        ...Connection.prototype.events,
        message(event) {
          const result = Connection.prototype.events.message.call(this, event);
          const {type, reason} = JSON.parse(event.data);
          if (type === INTERNAL.message_types.disconnect && reason === INTERNAL.disconnect_reasons.unauthorized) {
            webSocket.handleUnauthorized();
          }
          return result;
        }
      };
    }
    return consumer;
  },
  handleUnauthorized() {
    // The token was replaced while this socket was open, so try the new one
    if (currentToken() !== socketToken) {
      webSocket.reconnect();
      return;
    }
    rejectedToken = socketToken;
    webSocket.onUnauthorized();
  },
  // Reopens the socket with the current token, or closes it if there is no token any more
  reconnect() {
    if (!consumer) return;
    const {connection} = consumer;
    if (currentToken() === socketToken && connection.isActive()) return;
    connection.monitor.stop();
    if (connection.webSocket) {
      // Drop the old socket right away, even if it is still connecting
      connection.uninstallEventHandlers();
      connection.webSocket.close();
      connection.webSocket = null;
      // With its handlers gone the socket's close event never fires, so tell the
      // subscriptions ourselves, as Connection.events.close would
      if (!connection.disconnected) {
        connection.disconnected = true;
        consumer.subscriptions.notifyAll('disconnected', {willAttemptReconnect: false});
      }
    }
    socketToken = '';
    consumer.ensureActiveConnection();
  },
  subscribe(callback, channelName = 'DefaultChannel', idParam = '') {
    return this.consumer().subscriptions.create(
      {channel: channelName, resource_id: idParam},
      {...defaultChannelHandler, callback}
    );
  }
};

export default webSocket;
